import React, { useState } from 'react';
import { CreditCard, Loader2, CheckCircle2, AlertCircle } from 'lucide-react';
import * as api from '../../api';
import { loadRazorpayScript } from '../../utils/razorpay';

export default function RazorpayCheckoutButton({
  amount = 100, // Amount in INR (e.g. 100 => ₹100 = 10000 paise)
  currency = 'INR',
  itemName = 'RAPMAP Node Access Pass',
  description = 'Razorpay Standard Web Checkout',
  customerName = 'Test User',
  customerEmail = 'test@razorpay.com',
  customerPhone = '9999999999',
  bookingId = null,
  subscriptionId = null,
  buttonText = 'Pay with Razorpay',
  className = '',
  style = {},
  onSuccess = null,
  onFailure = null,
  onDismiss = null,
}) {
  const [loading, setLoading] = useState(false);
  const [statusMessage, setStatusMessage] = useState(null); // { type: 'success'|'error'|'info', text: string }

  const handleCheckout = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    setLoading(true);
    setStatusMessage(null);

    try {
      // 1. Ensure Razorpay Checkout SDK is ready
      const isScriptLoaded = await loadRazorpayScript();
      if (!isScriptLoaded || typeof window.Razorpay === 'undefined') {
        throw new Error('Razorpay SDK failed to load. Please check your internet connection.');
      }

      // Convert INR amount to paise (1 INR = 100 paise). Minimum 100 paise.
      const amountInPaise = Math.max(100, Math.round(Number(amount) * 100));

      // 2. Call backend order creation endpoint: POST /api/create-order
      const orderData = await api.createRazorpayOrder({
        amount: amountInPaise,
        currency,
        receipt: `rcpt_${Date.now()}`,
        notes: {
          itemName,
          bookingId: bookingId || 'standalone_checkout',
        },
      });

      const orderId = orderData.order_id || orderData.id || orderData.data?.order_id;
      if (!orderId) {
        throw new Error(orderData.message || 'Failed to generate Razorpay order ID from backend.');
      }

      // 3. Configure Razorpay options
      const keyId =
        process.env.REACT_APP_RAZORPAY_KEY_ID ||
        orderData.key_id ||
        orderData.data?.key_id;

      if (!keyId) {
        throw new Error('Razorpay Key ID is not configured in frontend or returned by backend.');
      }

      const options = {
        key: keyId,
        amount: orderData.amount || amountInPaise,
        currency: orderData.currency || currency,
        name: 'RAPMAP Platform',
        description: `${itemName} - ${description}`,
        image: 'https://images.unsplash.com/photo-1514525253161-7a46d19cd819?auto=format&fit=crop&w=128&q=80',
        order_id: orderId,
        handler: async function (response) {
          // STEP 2 & 3: On success, receive razorpay_payment_id, razorpay_order_id, razorpay_signature
          // Send all three to backend verify endpoint
          try {
            setStatusMessage({ type: 'info', text: 'Verifying payment signature with server...' });
            
            const verifyPayload = {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              bookingId,
              subscriptionId,
              amount: (orderData.amount || amountInPaise) / 100,
            };

            const verifyResult = await api.verifyRazorpayPayment(verifyPayload);

            if (verifyResult.success || verifyResult.verified) {
              setStatusMessage({
                type: 'success',
                text: `Payment verified successfully! Payment ID: ${response.razorpay_payment_id}`,
              });

              if (onSuccess) {
                onSuccess({
                  paymentId: response.razorpay_payment_id,
                  orderId: response.razorpay_order_id,
                  signature: response.razorpay_signature,
                  verification: verifyResult,
                });
              }
            } else {
              throw new Error(verifyResult.message || 'Signature verification failed on backend');
            }
          } catch (verifyError) {
            console.error('Payment verification failed:', verifyError);
            setStatusMessage({
              type: 'error',
              text: `Verification failed: ${verifyError.message}`,
            });
            if (onFailure) onFailure(verifyError);
          } finally {
            setLoading(false);
          }
        },
        prefill: {
          name: customerName,
          email: customerEmail,
          contact: customerPhone,
        },
        notes: {
          bookingId: bookingId || 'none',
        },
        theme: {
          color: '#F59E0B', // Cyber Amber theme
        },
        modal: {
          // Handle modal dismiss (user cancelled)
          ondismiss: function () {
            setLoading(false);
            setStatusMessage({
              type: 'info',
              text: 'Payment cancelled: Checkout window was closed.',
            });
            if (onDismiss) onDismiss();
          },
        },
      };

      const rzpInstance = new window.Razorpay(options);

      // Handle payment.failed event
      rzpInstance.on('payment.failed', function (failureResponse) {
        console.error('Razorpay payment failed:', failureResponse.error);
        setLoading(false);
        const errMsg =
          failureResponse.error?.description ||
          failureResponse.error?.reason ||
          'Payment failed. Please try again.';
        setStatusMessage({
          type: 'error',
          text: `Payment Failed: ${errMsg}`,
        });
        if (onFailure) onFailure(failureResponse.error);
      });

      // Open Razorpay payment modal
      rzpInstance.open();
    } catch (err) {
      console.error('Checkout initiation failed:', err);
      setLoading(false);
      setStatusMessage({
        type: 'error',
        text: err.message || 'Could not initiate Razorpay checkout.',
      });
      if (onFailure) onFailure(err);
    }
  };

  return (
    <div style={{ display: 'inline-flex', flexDirection: 'column', gap: '8px', ...style }}>
      <button
        type="button"
        onClick={handleCheckout}
        disabled={loading}
        className={`cyber-btn ${className}`}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px',
          background: 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)',
          color: '#000000',
          fontWeight: 800,
          border: 'none',
          borderRadius: '8px',
          padding: '10px 18px',
          fontSize: '0.9rem',
          cursor: loading ? 'not-allowed' : 'pointer',
          boxShadow: '0 4px 14px rgba(245, 158, 11, 0.35)',
          transition: 'all 0.2s ease',
          opacity: loading ? 0.7 : 1,
        }}
      >
        {loading ? (
          <>
            <Loader2 size={16} className="animate-spin" /> Processing Order...
          </>
        ) : (
          <>
            <CreditCard size={16} /> {buttonText} (₹{amount})
          </>
        )}
      </button>

      {statusMessage && (
        <div
          style={{
            fontSize: '0.8rem',
            padding: '8px 12px',
            borderRadius: '6px',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            background:
              statusMessage.type === 'success'
                ? 'rgba(16, 185, 129, 0.15)'
                : statusMessage.type === 'error'
                ? 'rgba(239, 68, 68, 0.15)'
                : 'rgba(59, 130, 246, 0.15)',
            color:
              statusMessage.type === 'success'
                ? '#10B981'
                : statusMessage.type === 'error'
                ? '#EF4444'
                : '#3B82F6',
            border: `1px solid ${
              statusMessage.type === 'success'
                ? 'rgba(16, 185, 129, 0.3)'
                : statusMessage.type === 'error'
                ? 'rgba(239, 68, 68, 0.3)'
                : 'rgba(59, 130, 246, 0.3)'
            }`,
          }}
        >
          {statusMessage.type === 'success' && <CheckCircle2 size={14} />}
          {statusMessage.type === 'error' && <AlertCircle size={14} />}
          <span>{statusMessage.text}</span>
        </div>
      )}
    </div>
  );
}
